import axios from 'axios'

const REFRESH_PATH = '/api-token-refresh/'

const getAccessToken = () => localStorage.getItem('accessToken')
const setAccessToken = (token) => localStorage.setItem('accessToken', token)
const getRefreshToken = () => localStorage.getItem('refreshToken')
const hasRefreshToken = () => {
  const token = getRefreshToken()
  return !!token && token !== ''
}

// Endpoints de autenticación: un 401 aquí NO debe disparar refresh
// (credenciales inválidas / flujo de login/refresh), debe burbujear tal cual.
const AUTH_PATHS = ['/api-token-auth', '/api-token-refresh', 'oauth', 'saml/login']
const isAuthEndpoint = (url = '') => AUTH_PATHS.some(path => url.includes(path))

// Handler de sesión expirada (el refresh falló). Por defecto: limpieza + redirect
// duro al login. App.vue registra uno más completo (websocket + router) en runtime.
let onSessionExpired = () => {
  try {
    setAccessToken('')
    localStorage.setItem('refreshToken', '')
  } catch (e) { /* noop */ }
  try {
    sessionStorage.setItem('force_logout_reason', 'Tu sesión expiró. Inicia sesión nuevamente.')
  } catch (e) { /* noop */ }
  if (typeof window !== 'undefined') window.location.href = '/login'
}

export const setSessionExpiredHandler = (handler) => {
  if (typeof handler === 'function') onSessionExpired = handler
}

// Refresh single-flight: todos los 401 concurrentes esperan la MISMA promesa,
// evitando una estampida de llamadas a /api-token-refresh.
let refreshPromise = null
const refreshAccessToken = () => {
  if (!refreshPromise) {
    // axios "crudo" (sin interceptores) para no recursar sobre el propio 401.
    // El refresh token viaja en el header Authorization; no lleva body.
    refreshPromise = axios
      .post(`${process.env.API_LAMBDA_URL}${REFRESH_PATH}`, null, {
        headers: { Authorization: `Bearer ${getRefreshToken()}` }
      })
      .then(response => {
        const newToken = response && response.data && response.data.access_token
        if (!newToken) throw new Error('Respuesta de refresh sin access_token')
        setAccessToken(newToken)
        return newToken
      })
      .finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

// AWS WAF token: la SDK del WAF (challenge.js, cargada en index.html) expone
// window.AwsWafIntegration. Adjuntamos el token en cada request para que el
// navegador resuelva el challenge del WAF en los endpoints protegidos
// (p.ej. /er-sales-data-query). En api.expertoretail.com el WAF lo valida;
// donde no aplica, el header se ignora sin efecto.
// Si la SDK aún no cargó o falla, seguimos SIN token para no romper la request:
// el WAF challengeará solo si corresponde.
const attachWafToken = async (config) => {
  try {
    const waf = typeof window !== 'undefined' && window.AwsWafIntegration
    if (waf && typeof waf.getToken === 'function') {
      config.headers['x-aws-waf-token'] = await waf.getToken()
    }
  } catch (e) { /* noop */ }
  return config
}

const onRequest = async (config) => {
  config.headers.Authorization = `Bearer ${getAccessToken()}`
  return attachWafToken(config)
}

const onResponse = (response) => response

const onErrorRequest = () => {
}

// Factory: cada instancia conserva su referencia para poder reintentar la request.
const makeOnErrorResponse = (axiosInstance) => async (error) => {
  if (error.response === undefined) {
    return Promise.reject('Ooops, algo anduvo mal por favor reintetar')
  }

  const status = error.response.status
  const originalRequest = error.config || {}

  // 403: lo maneja el interceptor global de App.vue (permisos). No tocar.
  if (status === 403) {
    return Promise.reject(error)
  }

  // 401: intentar refrescar el token UNA vez y reintentar la request original.
  if (
    status === 401 &&
    !originalRequest._retry &&
    hasRefreshToken() &&
    !isAuthEndpoint(originalRequest.url)
  ) {
    originalRequest._retry = true
    try {
      await refreshAccessToken()
      // onRequest reinyecta el Authorization con el token nuevo en el reintento.
      return axiosInstance(originalRequest)
    } catch (refreshError) {
      onSessionExpired()
      return Promise.reject(error.response)
    }
  }

  return Promise.reject(error.response)
}

const ClientApiPrototype = function (url) {
  const axiosInstance = axios.create({
    baseURL: url
  })
  axiosInstance.interceptors.request.use(onRequest, onErrorRequest)
  axiosInstance.interceptors.response.use(onResponse, makeOnErrorResponse(axiosInstance))
  return axiosInstance
}

const ClientLambdaApi = ClientApiPrototype(process.env.API_LAMBDA_URL)
const ClientErAdminData = ClientApiPrototype(process.env.API_ER_ADMIN_DATA_URL)
const ClientBackGroundTasks = ClientApiPrototype(process.env.API_BACKGROUND_TASKS_URL)

const CustomClient = axios

export { ClientLambdaApi, CustomClient, ClientErAdminData, ClientBackGroundTasks }



// WEBPACK FOOTER //
// ./src/api-client.js